import React from "react";

/**
 * Tooltip component.
 * Usage:
 *   <Tooltip text="Your account">
 *     <SomeTriggerElement />
 *   </Tooltip>
 *
 * Props:
 *   text      — tooltip label
 *   position  — "top" (default) | "bottom" | "left" | "right"
 *   icon      — whether to show the person icon (default: true)
 *   children  — the element that triggers the tooltip on hover
 */
export default function Tooltip({ text, position = "right", icon = true, children }) {
  const positionClasses = {
    top: "bottom-full left-1/2 -translate-x-1/2 mb-2",
    bottom: "top-full left-1/2 -translate-x-1/2 mt-2",
    left: "right-full top-1/2 -translate-y-1/2 mr-2",
    right: "left-full top-1/2 -translate-y-1/2 ml-2",
  };

  const arrowClasses = {
    top: "top-full left-1/2 -translate-x-1/2 border-l-transparent border-r-transparent border-b-transparent border-t-primary-blue",
    bottom: "bottom-full left-1/2 -translate-x-1/2 border-l-transparent border-r-transparent border-t-transparent border-b-primary-blue",
    left: "left-full top-1/2 -translate-y-1/2 border-t-transparent border-b-transparent border-r-transparent border-l-primary-blue",
    right: "right-full top-1/2 -translate-y-1/2 border-t-transparent border-b-transparent border-l-transparent border-r-primary-blue",
  };

  return (
    <span className="relative group inline-flex items-center">
      {children}
      <span
        className={`absolute z-50 ${positionClasses[position]} pointer-events-none`}
      >
        {/* Bubble */}
        <span className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-primary-blue text-white text-[11px] font-medium whitespace-nowrap shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-150">
          {icon && (
            <svg className="w-3 h-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
            </svg>
          )}
          {text}
        </span>
        {/* Arrow */}
        <span
          className={`absolute border-4 ${arrowClasses[position]} opacity-0 group-hover:opacity-100 transition-opacity duration-150`}
        />
      </span>
    </span>
  );
}
